// The last 20 Cowork skill runs for the practice, newest first.
//
// `notes` is written by the skill and holds counts and claim ids only (C's rule and
// A's column comment). It is shown as text, never as HTML.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchRuns, parseServerTime } from "./api";
import { relativeTime } from "./ageing";
import "./Queue.css";

const STATUS_CLASS = {
  running: "sq-status-queued",
  finished: "sq-status-submitted",
  failed: "sq-status-failed",
  abandoned: "sq-status-failed",
};

function duration(run) {
  const start = parseServerTime(run.startedAt);
  const end = parseServerTime(run.finishedAt);
  if (!start || !end) return null;
  const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
  if (minutes < 1) return "under a minute";
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

export default function RunsList({ practiceId }) {
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const ticket = useRef(0);

  const load = useCallback(async () => {
    if (!practiceId) return;
    const mine = (ticket.current += 1);
    setLoading(true);
    setError(null);
    try {
      const body = await fetchRuns(practiceId);
      if (mine !== ticket.current) return;
      setRuns(body);
    } catch (err) {
      if (mine !== ticket.current) return;
      setError(err.message);
      setRuns(null);
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
  }, [practiceId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="sq-runs">
      <div className="sq-tab-toolbar">
        <span className="sq-muted">Last 20 runs, newest first.</span>
        <button
          className="btn btn-secondary btn-sm"
          onClick={load}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="sq-message error" role="alert">
          <span>{error}</span>
        </div>
      )}

      {loading && !runs && <p className="sq-empty">Loading runs…</p>}

      {runs && runs.length === 0 && (
        <p className="sq-empty">
          The Cowork skill has never run for this practice.
        </p>
      )}

      {runs && runs.length > 0 && (
        <div className="sq-table-wrap">
          <table className="sq-table">
            <thead>
              <tr>
                <th>Run</th>
                <th>Started</th>
                <th>Status</th>
                <th>Operator</th>
                <th className="num">Claimed</th>
                <th className="num">Submitted</th>
                <th className="num">Failed</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr
                  key={run.id}
                  className={`sq-row ${run.needsHuman ? "amber" : ""}`}
                >
                  <td className="sq-claim">#{run.id}</td>
                  <td className="sq-nowrap">
                    <div>
                      {run.startedAt ? relativeTime(run.startedAt) : "—"}
                    </div>
                    {duration(run) && (
                      <div className="sq-muted">took {duration(run)}</div>
                    )}
                  </td>
                  <td>
                    <span
                      className={`sq-status ${STATUS_CLASS[run.status] || ""}`}
                    >
                      {run.status}
                    </span>
                    {run.needsHuman && (
                      <div>
                        <span className="sq-age-chip amber">
                          needs a person
                        </span>
                      </div>
                    )}
                  </td>
                  <td>{run.operator || "—"}</td>
                  <td className="num">{run.claimedCount}</td>
                  <td className="num">{run.submittedCount}</td>
                  <td className="num">{run.failedCount}</td>
                  <td className="sq-run-notes">{run.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
